import React, {
    useCallback,
    useEffect,
    useState
} from "react";

import {
    useGetAllQAQuestionsMutation,
    useGetQAQuestionMutation,
    useGetAllQASectionsMutation,
    useDeleteQAQuestionMutation
} from "./Api";

import AddQAQuestion from "./AddQAQuestion";

import Loader from "../loader/loader";
import ToastrService from "../../services/toastService";
import {
    FaEdit,
    FaEye,
    FaTrash
} from "react-icons/fa";
const QAQuestionManagement = () => {

    /*
     * List filters
     */
    const [search, setSearch] =
        useState("");

    const [debouncedSearch, setDebouncedSearch] =
        useState("");

    const [status, setStatus] =
        useState("");

    const [sectionId, setSectionId] =
        useState("");

    /*
     * Pagination
     */
    const [page, setPage] =
        useState(1);

    const [limit, setLimit] =
        useState(10);

    /*
     * Question list
     */
    const [questions, setQuestions] =
        useState([]);

    const [pagination, setPagination] =
        useState({
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false
        });

    /*
     * Section dropdown
     */
    const [sections, setSections] =
        useState([]);

    /*
     * Drawer state
     */
    const [
        isQuestionDrawerOpen,
        setIsQuestionDrawerOpen
    ] = useState(false);

    const [
        questionDrawerMode,
        setQuestionDrawerMode
    ] = useState("add");

    const [
        selectedQuestion,
        setSelectedQuestion
    ] = useState(null);

    /*
     * Refresh trigger
     */
    const [refresh, setRefresh] =
        useState(false);

    /*
     * APIs
     */
    const [
        getAllQAQuestionsApi,
        {
            isLoading: questionListLoading
        }
    ] = useGetAllQAQuestionsMutation();

    const [
        getQAQuestionApi,
        {
            isLoading: questionDetailsLoading
        }
    ] = useGetQAQuestionMutation();

    const [
        getAllQASectionsApi,
        {
            isLoading: sectionListLoading
        }
    ] = useGetAllQASectionsMutation();

    const [
        deleteQAQuestionApi,
        {
            isLoading: deleteQuestionLoading
        }
    ] = useDeleteQAQuestionMutation();

    const isLoading =
        questionListLoading ||
        questionDetailsLoading ||
        sectionListLoading;

    /*
     * Debounce search
     */
    useEffect(() => {

        const timeout =
            setTimeout(() => {

                setDebouncedSearch(
                    search.trim()
                );

                setPage(1);

            }, 500);

        return () =>
            clearTimeout(timeout);

    }, [search]);

    /*
     * Extract list rows from different
     * possible API response formats.
     */
    const extractRows = (
        responseData
    ) => {
        if (
            Array.isArray(
                responseData
            )
        ) {
            return responseData;
        }

        if (
            Array.isArray(
                responseData?.rows
            )
        ) {
            return responseData.rows;
        }

        if (
            Array.isArray(
                responseData?.data
            )
        ) {
            return responseData.data;
        }

        if (
            Array.isArray(
                responseData?.records
            )
        ) {
            return responseData.records;
        }
        if (
            Array.isArray(
                responseData?.sections
            )
        ) {
            return responseData.sections;
        }

        return [];

    };

    
    /*
     * Convert Sequelize instance to
     * plain object when needed.
     */
    const normalizeRow = (
        item
    ) => {

        if (
            item &&
            typeof item.get === "function"
        ) {
            return item.get({
                plain: true
            });
        }

        return item;

    };

    /*
     * Get API error message
     */
    const getErrorMessage = (
        response,
        defaultMessage
    ) => {

        return (
            response?.error?.data?.message ||
            response?.data?.message ||
            response?.message ||
            defaultMessage
        );

    };

    /*
     * Get all QA sections for filter
     * and AddQAQuestion dropdown.
     */
    const getAllSections =
        useCallback(
            async () => {

                try {

                    const response =
                        await getAllQASectionsApi({
                            page: 1,
                            limit: 1000,
                            status: "active",
                            search: ""
                        });

                    if (
                        !response?.data?.success
                    ) {

                        ToastrService.error(
                            getErrorMessage(
                                response,
                                "Unable to retrieve QA sections"
                            )
                        );

                        setSections([]);

                        return;

                    }

                    const responseData =
                        response.data.data;


                    const rows =
                        extractRows(
                            responseData
                        ).map(
                            normalizeRow
                        );
                    console.log('rows ', rows)
                    setSections(rows);

                } catch (error) {

                    console.error(
                        "Get QA sections error:",
                        error
                    );

                    ToastrService.error(
                        error?.data?.message ||
                        error?.message ||
                        "Unable to retrieve QA sections"
                    );

                    setSections([]);

                }

            },
            [
                getAllQASectionsApi
            ]
        );


    /*
     * Get all QA questions
     */
    const getAllQuestions =
        useCallback(
            async () => {

                try {
                    const payload = {
                        page,
                        limit,
                        search:
                            debouncedSearch,
                        status:
                            status || undefined,
                        section_id:
                            sectionId
                                ? Number(
                                    sectionId
                                )
                                : undefined
                    };

                    const response =
                        await getAllQAQuestionsApi(
                            payload
                        );

                    if (
                        !response?.data?.success
                    ) {

                        ToastrService.error(
                            getErrorMessage(
                                response,
                                "Unable to retrieve QA questions"
                            )
                        );

                        setQuestions([]);

                        setPagination({
                            page,
                            limit,
                            total: 0,
                            totalPages: 0,
                            hasNextPage: false,
                            hasPreviousPage: false
                        });

                        return;

                    }

                    const responseData =
                        response.data.data;

                    const rows =
                        extractRows(
                            responseData
                        ).map(
                            normalizeRow
                        );

                    setQuestions(rows);

                    const paginationData =
                        responseData?.pagination ||
                        responseData;

                    const total =
                        Number(
                            paginationData?.total ??
                            paginationData?.count ??
                            rows.length ??
                            0
                        );

                    const totalPages =
                        Number(
                            paginationData
                                ?.totalPages ??
                            Math.ceil(
                                total / limit
                            )
                        );

                    setPagination({
                        page:
                            Number(
                                paginationData
                                    ?.page ||
                                page
                            ),

                        limit:
                            Number(
                                paginationData
                                    ?.limit ||
                                limit
                            ),

                        total,

                        totalPages,

                        hasNextPage:
                            paginationData
                                ?.hasNextPage ??
                            page < totalPages,

                        hasPreviousPage:
                            paginationData
                                ?.hasPreviousPage ??
                            page > 1
                    });

                } catch (error) {

                    console.error(
                        "Get QA questions error:",
                        error
                    );

                    ToastrService.error(
                        error?.data?.message ||
                        error?.message ||
                        "Unable to retrieve QA questions"
                    );

                    setQuestions([]);

                }

            },
            [
                page,
                limit,
                status,
                sectionId,
                debouncedSearch,
                refresh,
                getAllQAQuestionsApi
            ]
        );


const handleDeleteQuestion =
    async (question) => {

        try {

            const confirmed =
                window.confirm(
                    `Are you sure you want to delete this question?\n\n${question.question_text}`
                );

            if (!confirmed) {
                return;
            }

            const response =
                await deleteQAQuestionApi({
                    id: question.id
                });

            if (
                !response?.data?.success
            ) {

                ToastrService.error(
                    getErrorMessage(
                        response,
                        "Unable to delete QA question"
                    )
                );

                return;
            }

            ToastrService.success(
                response?.data?.message ||
                "QA Question deleted successfully"
            );

            /*
             * If current page has only
             * one row and page > 1,
             * go to previous page.
             */
            if (
                questions.length === 1 &&
                page > 1
            ) {

                setPage(
                    previous =>
                        previous - 1
                );

                return;
            }

            /*
             * Refresh current page
             */
            setRefresh(
                previous =>
                    !previous
            );

        } catch (error) {

            console.error(
                "Delete QA question error:",
                error
            );

            ToastrService.error(
                error?.data?.message ||
                error?.message ||
                "Unable to delete QA question"
            );

        }

    };

    /*
     * Initial section load
     */
    useEffect(() => {

        getAllSections();

    }, [getAllSections]);

    /*
     * Reload question list when
     * filter, page or refresh changes.
     */
    useEffect(() => {

        getAllQuestions();

    }, [getAllQuestions]);

    /*
     * Add question drawer
     */
    const handleAddQuestion = () => {

        setQuestionDrawerMode(
            "add"
        );

        setSelectedQuestion(
            null
        );

        setIsQuestionDrawerOpen(
            true
        );

    };

    /*
     * Fetch question details and
     * open edit/view drawer.
     */
    const openQuestionDrawer =
        async (
            id,
            mode
        ) => {

            try {

                if (!id) {

                    ToastrService.error(
                        "Question ID is required"
                    );

                    return;

                }

                const response =
                    await getQAQuestionApi({
                        id
                    });

                if (
                    !response?.data?.success
                ) {

                    ToastrService.error(
                        getErrorMessage(
                            response,
                            "Unable to retrieve QA question"
                        )
                    );

                    return;

                }

                const responseData =
                    response.data.data;

                const questionData =
                    responseData?.question ||
                    responseData;

                if (!questionData) {

                    ToastrService.error(
                        "QA question not found"
                    );

                    return;

                }

                setSelectedQuestion(
                    normalizeRow(
                        questionData
                    )
                );

                setQuestionDrawerMode(
                    mode
                );

                setIsQuestionDrawerOpen(
                    true
                );

            } catch (error) {

                console.error(
                    "Get QA question error:",
                    error
                );

                ToastrService.error(
                    error?.data?.message ||
                    error?.message ||
                    "Unable to retrieve QA question"
                );

            }

        };

    /*
     * Edit question
     */
    const handleEditQuestion = (
        item
    ) => {

        openQuestionDrawer(
            item.id,
            "edit"
        );

    };

    /*
     * View question
     */
    const handleViewQuestion = (
        item
    ) => {

        openQuestionDrawer(
            item.id,
            "view"
        );

    };

    /*
     * Close question drawer
     */
    const handleCloseQuestionDrawer =
        () => {

            setIsQuestionDrawerOpen(
                false
            );

            setQuestionDrawerMode(
                "add"
            );

            setSelectedQuestion(
                null
            );

        };

    /*
     * Save callback
     */
    const handleQuestionSave = () => {

        handleCloseQuestionDrawer();

        setRefresh(
            (previous) =>
                !previous
        );

    };

    /*
     * Filter handlers
     */
    const handleStatusChange = (
        event
    ) => {

        setStatus(
            event.target.value
        );

        setPage(1);

    };

    const handleSectionChange = (
        event
    ) => {
        setSectionId(
            event.target.value
        );

        setPage(1);

    };

    const handleLimitChange = (
        event
    ) => {

        setLimit(
            Number(
                event.target.value
            )
        );

        setPage(1);

    };

    /*
     * Clear filters
     */
    const handleClearFilters = () => {

        setSearch("");

        setDebouncedSearch("");

        setStatus("");

        setSectionId("");

        setPage(1);

    };

    /*
     * Pagination handlers
     */
    const handlePreviousPage = () => {

        if (
            page > 1
        ) {
            setPage(
                (previous) =>
                    previous - 1
            );
        }

    };

    const handleNextPage = () => {

        if (
            page <
            pagination.totalPages
        ) {
            setPage(
                (previous) =>
                    previous + 1
            );
        }

    };

    const handlePageChange = (
        pageNumber
    ) => {

        if (
            pageNumber >= 1 &&
            pageNumber <=
            pagination.totalPages
        ) {
            setPage(pageNumber);
        }

    };

    /*
     * Build visible page numbers.
     */
    const getPageNumbers = () => {

        const totalPages =
            pagination.totalPages;

        if (
            totalPages <= 1
        ) {
            return [];
        }

        const startPage =
            Math.max(
                1,
                page - 2
            );

        const endPage =
            Math.min(
                totalPages,
                page + 2
            );

        const pages = [];

        for (
            let currentPage =
                startPage;
            currentPage <= endPage;
            currentPage += 1
        ) {
            pages.push(
                currentPage
            );
        }

        return pages;

    };

    /*
     * Question type label
     */
    const getQuestionTypeLabel = (
        type
    ) => {

        const typeLabels = {
            yes_no: "Yes / No",
            rating: "Rating",
            dropdown: "Dropdown",
            multiple_choice:
                "Multiple Choice",
            checkbox: "Checkbox",
            text: "Text Answer",
            critical_violation: "Critical Violation"
        };

        return (
            typeLabels[type] ||
            type ||
            "-"
        );

    };

    /*
     * Boolean display
     */
    const isTrueValue = (
        value
    ) => {

        return (
            value === true ||
            value === 1 ||
            value === "1" ||
            value === "true"
        );

    };

    /*
     * Get section name
     */
    const getSectionName = (
        question
    ) => {

        return (
            question?.section
                ?.section_name ||
            question
                ?.QASection
                ?.section_name ||
            question
                ?.qa_section
                ?.section_name ||
            question
                ?.section_name ||
            sections.find(
                (section) =>
                    Number(
                        section.id
                    ) ===
                    Number(
                        question.section_id
                    )
            )?.section_name ||
            "-"
        );

    };

    return (
        <div className="container-fluid py-3">

            {
                isLoading && (
                    <Loader />
                )
            }

            {/* Header */}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">

                <div>

                    <h3 className="mb-1">
                        QA Question Management
                    </h3>

                    <p className="text-muted mb-0">
                        Create and manage evaluation questions under QA sections.
                    </p>

                </div>

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={
                        handleAddQuestion
                    }
                >
                    Add Question
                </button>

            </div>

            {/* Filters */}

            <div className="card border-0 shadow-sm mb-4">

                <div className="card-body">

                    <div className="row g-3 align-items-end">

                        <div className="col-xl-4 col-lg-4 col-md-6">

                            <label
                                htmlFor="questionSearch"
                                className="form-label"
                            >
                                Search
                            </label>

                            <input
                                type="text"
                                id="questionSearch"
                                className="form-control"
                                placeholder="Search question..."
                                value={
                                    search
                                }
                                onChange={
                                    (
                                        event
                                    ) =>
                                        setSearch(
                                            event
                                                .target
                                                .value
                                        )
                                }
                            />

                        </div>

                        <div className="col-xl-3 col-lg-3 col-md-6">

                            <label
                                htmlFor="sectionFilter"
                                className="form-label"
                            >
                                Section
                            </label>

                            <select
                                id="sectionFilter"
                                className="form-select form-select-sm"
                                value={
                                    sectionId
                                }
                                onChange={
                                    handleSectionChange
                                }
                            >
                                <option value="">
                                    All Sections
                                </option>

                                {
                                    sections.map(
                                        (
                                            section
                                        ) => (
                                            <option
                                                key={
                                                    section.id
                                                }
                                                value={
                                                    section.id
                                                }
                                            >
                                                {
                                                    section.section_name
                                                }
                                            </option>
                                        )
                                    )
                                }

                            </select>

                        </div>

                        <div className="col-xl-2 col-lg-2 col-md-6">

                            <label
                                htmlFor="statusFilter"
                                className="form-label"
                            >
                                Status
                            </label>

                            <select
                                id="statusFilter"
                                className="form-select form-select-sm"
                                value={
                                    status
                                }
                                onChange={
                                    handleStatusChange
                                }
                            >
                                <option value="">
                                    All Status
                                </option>

                                <option value="active">
                                    Active
                                </option>

                                <option value="inactive">
                                    Inactive
                                </option>

                            </select>

                        </div>

                        <div className="col-xl-2 col-lg-2 col-md-6">

                            <label
                                htmlFor="pageLimit"
                                className="form-label"
                            >
                                Records
                            </label>

                            <select
                                id="pageLimit"
                                className="form-select form-select-sm"
                                value={
                                    limit
                                }
                                onChange={
                                    handleLimitChange
                                }
                            >
                                <option value="10">
                                    10
                                </option>

                                <option value="25">
                                    25
                                </option>

                                <option value="50">
                                    50
                                </option>

                                <option value="100">
                                    100
                                </option>

                            </select>

                        </div>

                        <div className="col-xl-1 col-lg-1 col-md-6">

                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                title="Clear filters"
                                onClick={
                                    handleClearFilters
                                }
                            >
                                Clear
                            </button>

                        </div>

                    </div>

                </div>

            </div>

            {/* Question Table */}

            <div className="card border-0 shadow-sm">

                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">

                                <tr>

                                    <th
                                        style={{
                                            width: "70px"
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        Question
                                    </th>

                                    <th>
                                        Section
                                    </th>

                                    <th>
                                        Type
                                    </th>

                                    <th className="text-center">
                                        Max Score
                                    </th>

                                    <th className="text-center">
                                        Order
                                    </th>

                                    <th className="text-center">
                                        Mandatory
                                    </th>

                                    <th className="text-center">
                                        Critical
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th
                                        className="text-end"
                                        style={{
                                            width: "150px"
                                        }}
                                    >
                                        Actions
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {
                                    questions.length >
                                        0
                                        ? (
                                            questions.map(
                                                (
                                                    item,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            item.id ||
                                                            index
                                                        }
                                                    >

                                                        <td>
                                                            {
                                                                (
                                                                    page -
                                                                    1
                                                                ) *
                                                                limit +
                                                                index +
                                                                1
                                                            }
                                                        </td>

                                                        <td>

                                                            <div
                                                                className="fw-semibold text-wrap"
                                                                style={{
                                                                    minWidth:
                                                                        "260px"
                                                                }}
                                                            >
                                                                {
                                                                    item
                                                                        .question_text ||
                                                                    "-"
                                                                }
                                                            </div>

                                                        </td>

                                                        <td>
                                                            {
                                                                getSectionName(
                                                                    item
                                                                )
                                                            }
                                                        </td>

                                                        <td>
                                                            <span class="badge bg-gray-200 text-dark">  {
                                                                getQuestionTypeLabel(
                                                                    item.question_type
                                                                )
                                                            }</span>

                                                        </td>

                                                        <td className="text-center fw-semibold">
                                                            {
                                                                Number(
                                                                    item.maximum_score ||
                                                                    0
                                                                )
                                                            }
                                                        </td>

                                                        <td className="text-center">
                                                            {
                                                                item.display_order ??
                                                                "-"
                                                            }
                                                        </td>

                                                        <td className="text-center">

                                                            {
                                                                isTrueValue(
                                                                    item.is_mandatory
                                                                )
                                                                    ? (
                                                                        <span className="badge bg-success">
                                                                            Yes
                                                                        </span>
                                                                    )
                                                                    : (
                                                                        <span className="badge bg-secondary">
                                                                            No
                                                                        </span>
                                                                    )
                                                            }

                                                        </td>

                                                        <td className="text-center">

                                                            {
                                                                isTrueValue(
                                                                    item.is_critical
                                                                )
                                                                    ? (
                                                                        <span className="badge bg-danger">
                                                                            Yes
                                                                        </span>
                                                                    )
                                                                    : (
                                                                        <span className="badge bg-secondary">
                                                                            No
                                                                        </span>
                                                                    )
                                                            }

                                                        </td>

                                                        <td>

                                                            <span
                                                                className={`badge ${item.status ===
                                                                    "active"
                                                                    ? "bg-success"
                                                                    : "bg-secondary"
                                                                    }`}
                                                            >
                                                                {
                                                                    item.status ===
                                                                        "active"
                                                                        ? "Active"
                                                                        : "Inactive"
                                                                }
                                                            </span>

                                                        </td>

                                                        <td className="text-end">

                                                            <div className="d-inline-flex gap-2">

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link text-dark p-0"
                                                                    title="View Question"
                                                                    onClick={() => handleViewQuestion(item)}
                                                                >
                                                                    <FaEye size={18} />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link text-dark p-0"
                                                                    title="Edit Question"
                                                                    onClick={() => handleEditQuestion(item)}
                                                                >
                                                                    <FaEdit size={18} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-link text-dark p-0"
                                                                    title="Delete Question"
                                                                    disabled={
                                                                        deleteQuestionLoading
                                                                    }
                                                                    onClick={() =>
                                                                        handleDeleteQuestion(
                                                                            item
                                                                        )
                                                                    }
                                                                >
                                                                    <FaTrash size={15} />
                                                                </button>

                                                            </div>

                                                        </td>

                                                    </tr>
                                                )
                                            )
                                        )
                                        : (
                                            <tr>

                                                <td
                                                    colSpan="10"
                                                    className="text-center py-5"
                                                >

                                                    <div className="text-muted">

                                                        <div className="fw-semibold mb-1">
                                                            No QA questions found
                                                        </div>

                                                        <small>
                                                            Add a question or change the selected filters.
                                                        </small>

                                                    </div>

                                                </td>

                                            </tr>
                                        )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>

                {/* Pagination */}

                <div className="card-footer bg-white border-top">

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">

                        <div className="text-muted small">

                            Showing{" "}

                            {
                                pagination.total > 0
                                    ? (
                                        page -
                                        1
                                    ) *
                                    limit +
                                    1
                                    : 0
                            }

                            {" "}to{" "}

                            {
                                Math.min(
                                    page *
                                    limit,
                                    pagination.total
                                )
                            }

                            {" "}of{" "}

                            {
                                pagination.total
                            }

                            {" "}questions

                        </div>

                        {
                            pagination.totalPages >
                            1 && (
                                <nav
                                    aria-label="Question pagination"
                                >

                                    <ul className="pagination pagination-sm mb-0">

                                        <li
                                            className={`page-item ${page <= 1
                                                ? "disabled"
                                                : ""
                                                }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                disabled={
                                                    page <= 1
                                                }
                                                onClick={
                                                    handlePreviousPage
                                                }
                                            >
                                                Previous
                                            </button>

                                        </li>

                                        {
                                            getPageNumbers().map(
                                                (
                                                    pageNumber
                                                ) => (
                                                    <li
                                                        key={
                                                            pageNumber
                                                        }
                                                        className={`page-item ${page ===
                                                            pageNumber
                                                            ? "active"
                                                            : ""
                                                            }`}
                                                    >

                                                        <button
                                                            type="button"
                                                            className="page-link"
                                                            onClick={() =>
                                                                handlePageChange(
                                                                    pageNumber
                                                                )
                                                            }
                                                        >
                                                            {
                                                                pageNumber
                                                            }
                                                        </button>

                                                    </li>
                                                )
                                            )
                                        }

                                        <li
                                            className={`page-item ${page >=
                                                pagination.totalPages
                                                ? "disabled"
                                                : ""
                                                }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                disabled={
                                                    page >=
                                                    pagination.totalPages
                                                }
                                                onClick={
                                                    handleNextPage
                                                }
                                            >
                                                Next
                                            </button>

                                        </li>

                                    </ul>

                                </nav>
                            )
                        }

                    </div>

                </div>

            </div>

            {/* Add / Edit / View Drawer */}

            {
                isQuestionDrawerOpen && (
                    <AddQAQuestion
                        mode={
                            questionDrawerMode
                        }
                        data={
                            selectedQuestion
                        }
                        selectedSectionId={
                            sectionId ||
                            null
                        }
                        onClose={
                            handleCloseQuestionDrawer
                        }
                        onSave={
                            handleQuestionSave
                        }
                    />
                )
            }

        </div>
    );

};

export default QAQuestionManagement;